import { PublicClientApplication, LogLevel } from "@azure/msal-browser";

// MSAL configuration
const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || "",
    // Use "organizations" for multi-tenant app - allows any Azure AD tenant to sign in
    // Do NOT use a specific tenant ID here as that restricts to single tenant
    authority: "https://login.microsoftonline.com/organizations",
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    // Redirect sign-in (mobile) returns to "/" and is processed there; the
    // router then sends the user to the dashboard. Hopping back to the
    // page that started the sign-in first is an extra navigation that only
    // adds failure points on mobile browsers.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            console.info(message);
            break;
          default:
            break;
        }
      },
    },
  },
};

// Login request scopes - minimal scopes for user authentication
// Intune/Graph operations use Application permissions (client_credentials) on the backend
export const loginRequest = {
  scopes: [
    "User.Read",  // Required for basic user profile
    "openid",     // Required for OIDC authentication
    "profile",    // Required for user profile claims
    "offline_access"  // Required for refresh tokens
  ],
};

// Azure Resource Manager scope. Used to acquire a delegated ARM token so the
// signed-in admin can assign Azure RBAC roles (e.g. 'Storage Blob Data
// Contributor') to ALICE's service principal during onboarding. The admin
// must hold Owner / User Access Administrator on the target scope for the
// ARM call to succeed.
export const armRequest = {
  scopes: ["https://management.azure.com/user_impersonation"],
};

// Graph API scopes for user-level operations (if any)
// Note: Most Intune operations use Application permissions on the backend
export const graphConfig = {
  scopes: [
    "User.Read"
  ],
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};

// Create MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Phones and tablets block or mangle the sign-in popup (iOS Safari, Android
// Chrome, in-app browsers), which surfaced as "Login failed" on mobile while
// laptops were fine. On those devices sign in with a full-page redirect.
// iPadOS identifies as Macintosh, so touch support is the tell there.
export const prefersRedirectAuth = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
};

// MSAL error codes meaning the popup itself could not be used (blocked or
// closed by the browser), as opposed to the user cancelling or a genuine
// auth failure. These are the cases worth retrying as a redirect.
const POPUP_UNUSABLE_CODES = new Set([
  "popup_window_error",
  "empty_window_error",
  "block_iframe_reload",
]);
export const isPopupUnusableError = (error) =>
  !!error && POPUP_UNUSABLE_CODES.has(error.errorCode);

export const isInteractionInProgressError = (error) =>
  !!error && error.errorCode === "interaction_in_progress";

// MSAL marks a tab as "interaction in progress" when a redirect sign-in
// starts and clears it when the response is processed. If the response is
// never processed (page navigated away before MSAL read the hash, tab
// restored, user backed out of Microsoft's page) the flag survives in
// sessionStorage and every later sign-in attempt in that tab throws
// interaction_in_progress. Clearing it lets the next attempt proceed.
const INTERACTION_STATUS_KEY = "msal.interaction.status";
export const clearStaleInteraction = () => {
  try {
    sessionStorage.removeItem(INTERACTION_STATUS_KEY);
    localStorage.removeItem(INTERACTION_STATUS_KEY);
  } catch {
    // Storage unavailable (private mode / blocked): nothing to clear.
  }
};

// Check if Azure AD is configured with a valid GUID
export const isAzureConfigured = () => {
  const clientId = process.env.REACT_APP_AZURE_CLIENT_ID;
  if (!clientId) return false;
  // Check if it's a valid GUID format (Azure client IDs are GUIDs)
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidRegex.test(clientId);
};
